<template>
  <section class="section section-observed intro-page">
    <div class="intro-layout">
      <aside class="intro-toc">
        <p class="toc-label">目录导航</p>
        <ul>
          <li v-for="section in allSections" :key="section.id">
            <button
              type="button"
              :class="{ active: activeId === section.id }"
              @click="selectSection(section.id)"
            >
              {{ section.title }}
            </button>
          </li>
        </ul>
      </aside>

      <div class="intro-main">
        <div
          v-if="activeSection"
          :key="activeSection.id"
          class="intro-block"
        >
          <h2>{{ activeSection.title }}</h2>

          <template v-if="activeSection.kind === 'page'">
            <div
              v-if="pageEntry(activeSection) && pageEntry(activeSection).bodyContent"
              class="rich-body"
              v-html="pageEntry(activeSection).bodyContent"
            />
            <p v-else class="empty-hint">暂无内容，请在管理后台「学会简介 → {{ activeSection.title }}」中维护。</p>
          </template>

          <template v-else-if="activeSection.kind === 'personnel'">
            <div v-if="pagedPersonnel.length" class="personnel-grid">
              <article
                v-for="person in pagedPersonnel"
                :key="person.entryId"
                class="personnel-card"
                :class="{ 'is-clickable': person.bodyContent || person.linkUrl }"
                @click="openPerson(person)"
              >
                <img
                  v-if="person.coverUrl"
                  :src="front.resolvePublicDisplayUrl(person.coverUrl)"
                  :alt="person.title"
                  class="personnel-avatar"
                />
                <h4>{{ person.title }}</h4>
                <p v-if="person.category" class="personnel-role">{{ person.category }}</p>
                <div
                  v-if="person.bodyContent"
                  class="personnel-bio"
                  v-html="person.bodyContent"
                />
              </article>
            </div>
            <p v-else class="empty-hint">暂无人员信息，请在管理后台维护。</p>
            <FrontPagination
              :page-num="listPageNum"
              :page-size="listPageSize"
              :total="personnelTotal"
              @change="onListPageChange"
            />
          </template>

          <template v-else-if="activeSection.kind === 'branches'">
            <p class="section-lead">
              中国古生物学会下设多个学术分会（专业委员会），各分会独立开展学术交流与科普工作。
            </p>
            <ol class="branch-link-grid">
              <li v-for="(branch, index) in branchList" :key="branch.associationId">
                <button type="button" @click="goBranch(branch)">
                  <span>{{ String(index + 1).padStart(2, '0') }}</span>
                  <strong>{{ branch.associationName }}</strong>
                </button>
              </li>
            </ol>
            <div class="section-more">
              <button type="button" class="text-link" @click="front.goPath('/structure')">查看组织机构 →</button>
            </div>
          </template>

          <template v-else-if="activeSection.kind === 'timeline'">
            <div v-if="timelinePreview.length" class="timeline-preview">
              <div v-for="node in timelinePreview" :key="node.entryId" class="timeline-item">
                <span class="timeline-year">{{ node.category || '' }}</span>
                <div>
                  <h4>{{ node.summary || node.title }}</h4>
                  <p>{{ stripHtml(node.bodyContent || node.summary || '') }}</p>
                </div>
              </div>
            </div>
            <p v-else class="empty-hint">暂无历史沿革节点，请在管理后台「学会沿革」中维护。</p>
            <div class="section-more">
              <button type="button" class="text-link" @click="front.goPath('/history')">查看完整沿革 →</button>
            </div>
          </template>

          <template v-else-if="activeSection.kind === 'gallery'">
            <div v-if="galleryPreview.length" class="gallery-preview">
              <figure v-for="photo in galleryPreview" :key="photo.entryId" class="is-clickable" @click="front.openCmsEntry(photo)">
                <img
                  v-if="photo.coverUrl"
                  :src="front.resolvePublicDisplayUrl(photo.coverUrl)"
                  :alt="photo.title"
                />
                <figcaption>{{ photo.title }}</figcaption>
              </figure>
            </div>
            <p v-else class="empty-hint">暂无相册照片，请在管理后台「历史相册」中维护。</p>
            <div class="section-more">
              <button type="button" class="text-link" @click="front.goPath('/gallery')">查看历史相册 →</button>
            </div>
          </template>

          <template v-else-if="activeSection.kind === 'awards'">
            <div v-if="pagedAwards.length" class="awards-list">
              <article v-for="(award, index) in pagedAwards" :key="index" class="award-card">
                <span class="award-year">{{ award.year }}</span>
                <div>
                  <h4>{{ award.awardName }}</h4>
                  <p v-if="award.winner">获奖人：{{ award.winner }}</p>
                  <div v-if="award.description" class="award-desc" v-html="award.description" />
                </div>
              </article>
            </div>
            <p v-else class="empty-hint">暂无获奖记录，请在管理后台维护。</p>
            <FrontPagination
              :page-num="listPageNum"
              :page-size="listPageSize"
              :total="awards.length"
              @change="onListPageChange"
            />
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { INTRO_PAGE_CODES, INTRO_SECTIONS } from '../../data/introSections'
import { branchSitePath } from '../../data/branchSite'
import { listPublicAssociations, listPublicCmsEntries, unwrapPublicPage, applyClientPageFallback } from '../../api/paleo'
import { sortCmsByHistoryTime } from '../../utils/cmsExtra'
import FrontPagination from '../../components/common/FrontPagination.vue'

function parseExtra(extraJson) {
  if (!extraJson) return {}
  try {
    return typeof extraJson === 'string' ? JSON.parse(extraJson) : (extraJson || {})
  } catch (error) {
    return {}
  }
}

export default {
  name: 'IntroPage',
  components: { FrontPagination },
  inject: ['front'],
  data() {
    return {
      activeId: INTRO_SECTIONS[0] ? INTRO_SECTIONS[0].id : 'overview',
      listPageNum: 1,
      listPageSize: 5,
      pages: [],
      personnel: [],
      awardsEntries: [],
      timelineEntries: [],
      galleryEntries: [],
      associations: []
    }
  },
  computed: {
    societyPages() {
      return this.pages.filter(item => (item.scope || 'society') === 'society')
    },
    societyPersonnel() {
      return this.personnel.filter(item => (item.scope || 'society') === 'society')
    },
    societyTimeline() {
      return this.timelineEntries
        .filter(item => (item.scope || 'society') === 'society')
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    },
    societyGallery() {
      return this.galleryEntries
        .filter(item => (item.scope || 'society') === 'society')
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    },
    extraPageSections() {
      return this.societyPages
        .filter(item => item.columnCode && String(item.columnCode).startsWith('intro_') && !INTRO_PAGE_CODES.includes(item.columnCode))
        .map(item => ({
          id: item.columnCode,
          title: item.title,
          kind: 'page',
          pageCode: item.columnCode
        }))
    },
    allSections() {
      return [...INTRO_SECTIONS, ...this.extraPageSections]
    },
    defaultSectionId() {
      return this.allSections[0] ? this.allSections[0].id : 'overview'
    },
    activeSection() {
      return this.allSections.find(item => item.id === this.activeId) || this.allSections[0] || null
    },
    branchList() {
      const fromApi = this.associations.filter(item => item.associationType === 'BRANCH' || (item.associationId && item.associationId !== 1))
      if (fromApi.length) return fromApi
      return (this.front.branchAssociations || [])
    },
    timelinePreview() {
      return this.societyTimeline.slice(0, 4)
    },
    galleryPreview() {
      return this.societyGallery.slice(0, 6)
    },
    awards() {
      return this.awardsEntries
        .filter(item => (item.scope || 'society') === 'society')
        .map(item => {
          const extra = parseExtra(item.extraJson)
          return {
            year: item.category || '',
            awardName: extra.awardName || item.title,
            winner: extra.winner || item.summary || '',
            description: item.bodyContent || ''
          }
        })
        .sort((a, b) => String(b.year).localeCompare(String(a.year)))
    },
    pagedAwards() {
      return applyClientPageFallback(this.awards, this.awards.length, this.listPageNum, this.listPageSize).rows
    },
    personnelTotal() {
      if (!this.activeSection || this.activeSection.kind !== 'personnel') return 0
      return this.personnelOf(this.activeSection).length
    },
    pagedPersonnel() {
      if (!this.activeSection || this.activeSection.kind !== 'personnel') return []
      const all = this.personnelOf(this.activeSection)
      return applyClientPageFallback(all, all.length, this.listPageNum, this.listPageSize).rows
    }
  },
  mounted() {
    window.addEventListener('hashchange', this.onHashChange)
    this.loadIntroData().then(() => {
      this.$nextTick(() => {
        this.syncFromHash()
      })
    })
  },
  beforeDestroy() {
    window.removeEventListener('hashchange', this.onHashChange)
  },
  methods: {
    async loadIntroData() {
      const fromFront = (moduleCode) => (this.front.dynamicCmsEntries || [])
        .filter(item => item.moduleCode === moduleCode)
        .map(item => this.front.mapCmsEntry(item))

      this.pages = fromFront('pages')
      this.personnel = fromFront('personnel')
      this.awardsEntries = fromFront('awards')
      this.timelineEntries = fromFront('timeline')
      this.galleryEntries = sortCmsByHistoryTime(
        fromFront('gallery'),
        (this.front.siteConfig && this.front.siteConfig.galleryHistorySort) || 'asc'
      )
      this.associations = this.front.bindableAssociations || []

      try {
        const mapRows = (result) => unwrapPublicPage(result).rows.map(item => this.front.mapCmsEntry(item))
        const [pages, personnel, awards, timeline, gallery, associations] = await Promise.all([
          listPublicCmsEntries({ moduleCode: 'pages', pageNum: 1, pageSize: 200 }),
          listPublicCmsEntries({ moduleCode: 'personnel', pageNum: 1, pageSize: 200 }),
          listPublicCmsEntries({ moduleCode: 'awards', pageNum: 1, pageSize: 200 }),
          listPublicCmsEntries({ moduleCode: 'timeline', pageNum: 1, pageSize: 200 }),
          listPublicCmsEntries({ moduleCode: 'gallery', pageNum: 1, pageSize: 200 }),
          listPublicAssociations()
        ])
        this.pages = mapRows(pages)
        this.personnel = mapRows(personnel)
        this.awardsEntries = mapRows(awards)
        this.timelineEntries = mapRows(timeline)
        this.galleryEntries = sortCmsByHistoryTime(
          mapRows(gallery),
          (this.front.siteConfig && this.front.siteConfig.galleryHistorySort) || 'asc'
        )
        this.associations = associations.data || this.associations
      } catch (error) {
        // 保留 front 已有数据
      }
    },
    pageEntry(section) {
      if (!section.pageCode) return null
      return this.societyPages.find(item => item.columnCode === section.pageCode) || null
    },
    personnelOf(section) {
      const group = section.personnelGroup || ''
      return this.societyPersonnel.filter(item => (item.summary || '其他') === group)
    },
    goBranch(branch) {
      this.front.goPath(branchSitePath(branch.associationId, 'home'))
    },
    openPerson(person) {
      if (person && person.entryId) {
        this.front.openCmsEntry(person)
      }
    },
    onListPageChange(page) {
      const next = Number(page)
      if (!Number.isFinite(next) || next < 1) return
      this.listPageNum = next
    },
    resolveSectionId(id) {
      if (id && this.allSections.some(item => item.id === id)) return id
      return this.defaultSectionId
    },
    selectSection(id, { updateHash = true } = {}) {
      const nextId = this.resolveSectionId(id)
      this.activeId = nextId
      this.listPageNum = 1
      if (updateHash && window.history && window.history.replaceState) {
        // 用 replaceState 更新 hash，避免 location.hash 触发浏览器锚点滚动
        window.history.replaceState(null, '', `#${nextId}`)
      }
    },
    syncFromHash() {
      const hash = (window.location.hash || '').replace(/^#/, '')
      const nextId = this.resolveSectionId(hash)
      this.selectSection(nextId, { updateHash: nextId !== hash })
    },
    onHashChange() {
      this.syncFromHash()
    },
    stripHtml(html) {
      return String(html || '').replace(/<[^>]+>/g, '').trim().slice(0, 120)
    }
  }
}
</script>

<style scoped>
.intro-page {
  max-width: var(--content-max);
}

.intro-layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 28px;
  align-items: start;
}

.intro-toc {
  position: sticky;
  top: 112px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  padding: 16px;
  background: #fff;
}

.toc-label {
  margin: 0 0 10px;
  color: var(--subtle);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.intro-toc ul {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 4px;
}

.intro-toc button {
  width: 100%;
  border: 0;
  border-radius: 4px;
  padding: 10px 12px;
  background: transparent;
  color: var(--subtle);
  text-align: left;
  cursor: pointer;
  font-size: 14px;
}

.intro-toc button.active,
.intro-toc button:hover {
  background: var(--primary);
  color: #fff;
  font-weight: 800;
}

.intro-main {
  display: grid;
  gap: 22px;
  min-width: 0;
}

.intro-block {
  border: 1px solid var(--border);
  border-top: 3px solid var(--gold);
  border-radius: var(--front-radius);
  padding: 28px 30px;
  background: #fff;
  box-shadow: var(--shadow-soft, 0 8px 24px rgba(0, 43, 73, 0.06));
  min-height: 280px;
}

.intro-block h2 {
  margin: 0 0 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
  color: var(--primary);
  font-size: 26px;
}

.section-lead {
  margin: 0 0 16px;
  color: var(--subtle);
  font-size: 14px;
  line-height: 1.7;
}

.rich-body {
  color: var(--text);
  line-height: 1.75;
  font-size: 15px;
}

.rich-body >>> img {
  max-width: 100%;
  height: auto;
}

.empty-hint {
  margin: 18px 0 0;
  color: var(--subtle);
  text-align: center;
  font-size: 14px;
}

.personnel-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.personnel-card {
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 16px;
  background: #fff;
}

.personnel-card.is-clickable {
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.personnel-card.is-clickable:hover {
  border-color: var(--primary);
  box-shadow: 0 8px 20px rgba(0, 43, 73, 0.1);
  transform: translateY(-2px);
}

.personnel-avatar {
  width: 64px;
  height: 64px;
  margin-bottom: 10px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--border);
}

.personnel-card h4 {
  margin: 0;
  color: var(--primary);
  font-size: 17px;
}

.personnel-role {
  margin: 6px 0 0;
  color: var(--secondary);
  font-size: 12px;
  font-weight: 800;
}

.personnel-bio {
  margin-top: 8px;
  color: var(--subtle);
  font-size: 12px;
  line-height: 1.6;
}

.branch-link-grid {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.branch-link-grid button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 14px;
  background: #fff;
  color: var(--primary);
  cursor: pointer;
  text-align: left;
}

.branch-link-grid button:hover {
  border-color: var(--primary);
  background: #f7fbff;
}

.branch-link-grid span {
  display: grid;
  flex: 0 0 32px;
  height: 32px;
  place-items: center;
  border-radius: 4px;
  background: var(--primary);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.branch-link-grid strong {
  font-size: 14px;
}

.timeline-preview {
  display: grid;
  gap: 18px;
}

.timeline-item {
  display: flex;
  gap: 16px;
  padding-left: 16px;
  border-left: 2px solid var(--primary);
}

.timeline-year {
  flex: 0 0 72px;
  color: var(--primary);
  font-size: 22px;
  font-style: italic;
  font-weight: 900;
}

.timeline-item h4 {
  margin: 0 0 6px;
  color: var(--primary);
}

.timeline-item p {
  margin: 0;
  color: var(--subtle);
  font-size: 13px;
  line-height: 1.6;
}

.gallery-preview {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.gallery-preview figure {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #fff;
}

.gallery-preview figure.is-clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-preview figure.is-clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 29, 54, 0.12);
}

.gallery-preview img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}

.gallery-preview figcaption {
  padding: 10px 12px;
  color: var(--primary);
  font-size: 13px;
  font-weight: 800;
}

.awards-list {
  display: grid;
  gap: 12px;
}

.award-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 14px;
  background: #f8fafc;
}

.award-year {
  flex: 0 0 auto;
  border-radius: 4px;
  padding: 4px 8px;
  background: var(--primary);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.award-card h4 {
  margin: 0 0 4px;
  color: var(--primary);
  font-size: 15px;
}

.award-card p,
.award-desc {
  margin: 0;
  color: var(--subtle);
  font-size: 13px;
  line-height: 1.6;
}

.section-more {
  margin-top: 18px;
  text-align: right;
}

.text-link {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--primary);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.text-link:hover {
  text-decoration: underline;
}

@media (max-width: 900px) {
  .intro-layout {
    grid-template-columns: 1fr;
  }

  .intro-toc {
    position: static;
  }

  .personnel-grid,
  .gallery-preview,
  .branch-link-grid {
    grid-template-columns: 1fr;
  }
}
</style>
